/** Angle de fuite, en degrés. 30° : l'usage des manuels québécois. */
export const FUITE_DEG = 30;
/**
 * Rapport de réduction des fuyantes. 0,5 — projection dite « cabinet ». Sans
 * réduction (k = 1), un cube paraît deux fois trop profond.
 */
export const FUITE_RATIO = 0.5;
const COS_F = Math.cos((FUITE_DEG * Math.PI) / 180) * FUITE_RATIO;
const SIN_F = Math.sin((FUITE_DEG * Math.PI) / 180) * FUITE_RATIO;
/** Projection cavalière. */
export function projeter(p) {
    return { x: p.x + COS_F * p.z, y: p.y + SIN_F * p.z };
}
/**
 * La direction qui VA VERS l'observateur, déduite de la projection et non
 * choisie à part.
 *
 * Deux points qui ne diffèrent que d'un multiple de ce vecteur se projettent
 * au MÊME endroit — c'est ce qui en fait la ligne de visée. La dériver de la
 * projection, plutôt que de la coder en dur, garantit que les arêtes cachées
 * restent justes si l'angle de fuite change un jour.
 *
 * Le SIGNE compte, et il s'est trompé une première fois. En projection
 * cavalière, `+z` est dessiné vers le haut et vers la droite : il s'ENFONCE
 * dans la page. La face la plus proche de nous est donc celle dont la normale
 * pointe vers `−z`, et le vecteur qui va vers l'observateur porte `z = −1`.
 * Avec le signe inverse, les arêtes cachées étaient exactement les arêtes
 * visibles — un dessin qui a l'air juste et qui ment. Un test le tient.
 */
export const REGARD = { x: COS_F, y: SIN_F, z: -1 };
function moins3(a, b) {
    return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
function croix(a, b) {
    return {
        x: a.y * b.z - a.z * b.y,
        y: a.z * b.x - a.x * b.z,
        z: a.x * b.y - a.y * b.x,
    };
}
function scalaire(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}
/**
 * Une face est-elle tournée vers l'observateur ?
 *
 * `sommets` doit être orienté dans le sens direct VU DE L'EXTÉRIEUR du solide,
 * pour que la normale calculée soit bien la sortante.
 */
export function faceVisible(sommets) {
    if (sommets.length < 3)
        return false;
    const [a, b, c] = sommets;
    const n = croix(moins3(b, a), moins3(c, a));
    return scalaire(n, REGARD) > 0;
}
/** Clé d'arête indépendante du sens de parcours. */
function cle(a, b) {
    return a < b ? `${a}|${b}` : `${b}|${a}`;
}
/**
 * Classe chaque arête du polyèdre en visible ou cachée.
 *
 * Une arête est cachée quand AUCUNE des faces qui la bordent n'est visible.
 * Une arête de silhouette — une face visible, une face cachée — reste tracée
 * en plein : c'est le contour du solide.
 */
export function classerAretes(p) {
    const visibilite = new Map();
    const ordre = [];
    for (const face of p.faces) {
        const pts = face.map((nom) => p.sommets.get(nom)).filter((v) => v !== undefined);
        if (pts.length !== face.length)
            continue;
        const vue = faceVisible(pts);
        for (let i = 0; i < face.length; i += 1) {
            const de = face[i];
            const a = face[(i + 1) % face.length];
            const k = cle(de, a);
            if (!visibilite.has(k)) {
                visibilite.set(k, vue);
                ordre.push({ de, a });
            }
            else if (vue) {
                visibilite.set(k, true);
            }
        }
    }
    return ordre.map(({ de, a }) => ({ de, a, cachee: visibilite.get(cle(de, a)) !== true }));
}
/* ------------------------------------------------------- corps de révolution */
/** Nombre de points d'une ellipse projetée. 48 : indistinguable d'une courbe. */
export const POINTS_ELLIPSE = 48;
/**
 * Le cercle horizontal de rayon `r` à la hauteur `y`, projeté — coupé en deux
 * demi-courbes : celle qui est DEVANT, et celle qui est DERRIÈRE.
 *
 * « Devant » se décide sur la profondeur vue par l'observateur, pas sur le
 * signe de `z` : c'est la même direction de regard qui sert aux arêtes des
 * polyèdres, donc les deux restent cohérents si l'angle de fuite change.
 */
export function ellipseHorizontale(r, y) {
    const devant = [];
    const derriere = [];
    /* La profondeur d'un point du cercle, vue par l'observateur. Le maximum est
       atteint au point le plus proche de nous. */
    const profondeur = (t) => scalaire({ x: r * Math.cos(t), y, z: r * Math.sin(t) }, REGARD);
    for (let i = 0; i <= POINTS_ELLIPSE; i += 1) {
        const t = (i / POINTS_ELLIPSE) * 2 * Math.PI;
        const p = projeter({ x: r * Math.cos(t), y, z: r * Math.sin(t) });
        (profondeur(t) >= 0 ? devant : derriere).push(p);
    }
    return { devant, derriere };
}
/**
 * Les deux points de tangence du contour d'un cylindre ou d'un cône — là où la
 * silhouette quitte la base.
 *
 * Ce sont les points du cercle dont la profondeur vue est nulle : ni devant ni
 * derrière, exactement sur le bord.
 */
export function tangentesBase(r, y) {
    /* La profondeur vaut r(cos t · REGARD.x + sin t · REGARD.z) + y · REGARD.y ;
       elle s'annule pour tan t = −REGARD.x / REGARD.z, aux termes en y près qui
       ne dépendent pas de t. */
    const t = Math.atan2(-REGARD.x, REGARD.z);
    return [
        { x: r * Math.cos(t), y, z: r * Math.sin(t) },
        { x: r * Math.cos(t + Math.PI), y, z: r * Math.sin(t + Math.PI) },
    ];
}
/* ------------------------------------------------------ construction des corps */
/** Les sommets d'un polygone régulier à `n` côtés, dans le plan `y`. */
function polygoneBase(n, cote, y) {
    // Rayon circonscrit d'un polygone régulier de côté `cote`.
    const r = cote / (2 * Math.sin(Math.PI / n));
    return Array.from({ length: n }, (_, i) => {
        const t = (2 * Math.PI * i) / n + Math.PI / n;
        return { x: r * Math.cos(t), y, z: r * Math.sin(t) };
    });
}
/** Les sommets d'une base rectangulaire `l × p`, centrée. */
function rectangleBase(l, p, y) {
    return [
        { x: -l / 2, y, z: -p / 2 },
        { x: l / 2, y, z: -p / 2 },
        { x: l / 2, y, z: p / 2 },
        { x: -l / 2, y, z: p / 2 },
    ];
}
/** Le nombre de côtés d'une base nommée. */
function nbCotes(base) {
    switch (base) {
        case 'triangle':
            return 3;
        case 'hexagone':
            return 6;
        default:
            return 4;
    }
}
/**
 * Les sommets de la base d'un prisme ou d'une pyramide, à la hauteur `y`.
 *
 * Rend `null` sur une base rectangulaire sans profondeur : un rectangle a
 * besoin de ses deux dimensions, et en deviner une serait dessiner autre chose
 * que ce que l'énoncé décrit.
 */
function sommetsBase(base, cote, profondeur, y) {
    if (base === 'rectangle') {
        if (profondeur === undefined || profondeur <= 0)
            return null;
        return rectangleBase(cote, profondeur, y);
    }
    if (base === 'carre')
        return rectangleBase(cote, cote, y);
    return polygoneBase(nbCotes(base), cote, y);
}
/**
 * Le polyèdre d'un solide à faces planes — cube, prisme, pyramide.
 *
 * Rend `null` pour les corps de révolution : ils n'ont pas d'arêtes à classer,
 * et leur silhouette se construit autrement (`tangentesBase`).
 */
export function polyedreDe(d) {
    if (d.forme === 'cylindre' || d.forme === 'cone' || d.forme === 'sphere')
        return null;
    const nom = (prefixe, i) => `${prefixe}${i + 1}`;
    if (d.forme === 'cube') {
        if (!(d.arete > 0))
            return null;
        return prismeDe(rectangleBase(d.arete, d.arete, 0), d.arete, nom);
    }
    if (d.forme === 'prisme_droit') {
        if (!(d.coteBase > 0) || !(d.hauteur > 0))
            return null;
        const bas = sommetsBase(d.base, d.coteBase, d.profondeurBase, 0);
        return bas === null ? null : prismeDe(bas, d.hauteur, nom);
    }
    // Pyramide.
    if (!(d.coteBase > 0) || !(d.hauteur > 0))
        return null;
    const bas = sommetsBase(d.base, d.coteBase, d.profondeurBase, 0);
    if (bas === null)
        return null;
    const sommets = new Map();
    bas.forEach((p, i) => sommets.set(nom('A', i), p));
    sommets.set('S', { x: 0, y: d.hauteur, z: 0 });
    const n = bas.length;
    const faces = [];
    /* CONVENTION D'ORIENTATION — chaque face est parcourue dans le sens direct
       VUE DU DEHORS, sans quoi la normale calculée rentre dans le solide au lieu
       d'en sortir et les arêtes cachées sont exactement les visibles.
       `sommetsBase` rend ses points dans un ordre dont la normale pointe déjà
       vers le bas : c'est donc l'ordre de la base, telle quelle. */
    faces.push(Array.from({ length: n }, (_, i) => nom('A', i)));
    for (let i = 0; i < n; i += 1) {
        faces.push(['S', nom('A', (i + 1) % n), nom('A', i)]);
    }
    return { sommets, faces };
}
/** Un prisme droit à partir de sa base et de sa hauteur. */
function prismeDe(bas, hauteur, nom) {
    const n = bas.length;
    const sommets = new Map();
    bas.forEach((p, i) => {
        sommets.set(nom('A', i), p);
        sommets.set(nom('S', i), { x: p.x, y: p.y + hauteur, z: p.z });
    });
    const faces = [];
    /* Même convention que pour la pyramide : sens direct vu du DEHORS. La base
       du bas garde l'ordre naturel (normale vers le bas) ; celle du haut est
       inversée (normale vers le haut) ; les faces latérales descendent du haut
       vers le bas pour que leur normale sorte du solide. */
    faces.push(Array.from({ length: n }, (_, i) => nom('A', i)));
    faces.push(Array.from({ length: n }, (_, i) => nom('S', n - 1 - i)));
    for (let i = 0; i < n; i += 1) {
        const j = (i + 1) % n;
        faces.push([nom('S', i), nom('S', j), nom('A', j), nom('A', i)]);
    }
    return { sommets, faces };
}
function rect(x, y, l, h) {
    return [
        { x, y },
        { x: x + l, y },
        { x: x + l, y: y + h },
        { x, y: y + h },
    ];
}
/**
 * Le développement d'un solide, ou `null` quand il n'en a pas.
 *
 * Une SPHÈRE rend `null`, et ce n'est pas une limite du tracé : la surface
 * d'une sphère n'est pas développable — c'est le théorème de Gauss, et c'est
 * la raison pour laquelle toute carte du monde déforme quelque chose. Un
 * refus, ici, enseigne quelque chose de juste.
 */
export function developpementDe(d) {
    if (d.forme === 'sphere')
        return null;
    const contours = [];
    const cercles = [];
    const secteurs = [];
    const plis = [];
    if (d.forme === 'cube') {
        if (!(d.arete > 0))
            return null;
        const a = d.arete;
        // La croix classique : quatre faces en bande, une au-dessus, une en dessous.
        for (let i = 0; i < 4; i += 1)
            contours.push(rect(i * a, 0, a, a));
        contours.push(rect(a, a, a, a));
        contours.push(rect(a, -a, a, a));
        for (let i = 1; i < 4; i += 1)
            plis.push([
                { x: i * a, y: 0 },
                { x: i * a, y: a },
            ]);
        plis.push([
            { x: a, y: a },
            { x: 2 * a, y: a },
        ]);
        plis.push([
            { x: a, y: 0 },
            { x: 2 * a, y: 0 },
        ]);
        return { contours, cercles, secteurs, plis };
    }
    if (d.forme === 'cylindre') {
        if (!(d.rayon > 0) || !(d.hauteur > 0))
            return null;
        const largeur = 2 * Math.PI * d.rayon;
        contours.push(rect(0, 0, largeur, d.hauteur));
        // Les deux disques, posés contre les bords du rectangle déroulé.
        cercles.push({ centre: { x: largeur / 2, y: d.hauteur + d.rayon }, rayon: d.rayon });
        cercles.push({ centre: { x: largeur / 2, y: -d.rayon }, rayon: d.rayon });
        plis.push([
            { x: 0, y: d.hauteur },
            { x: largeur, y: d.hauteur },
        ]);
        plis.push([
            { x: 0, y: 0 },
            { x: largeur, y: 0 },
        ]);
        return { contours, cercles, secteurs, plis };
    }
    if (d.forme === 'cone') {
        if (!(d.rayon > 0) || !(d.hauteur > 0))
            return null;
        /* L'apothème — la génératrice — se DÉDUIT, elle n'est jamais donnée : le
           contrat interdit au modèle de fournir une longueur dérivable. */
        const apotheme = Math.hypot(d.rayon, d.hauteur);
        const angleDeg = (360 * d.rayon) / apotheme;
        secteurs.push({ centre: { x: 0, y: 0 }, rayon: apotheme, debutDeg: 0, finDeg: angleDeg });
        cercles.push({ centre: { x: 0, y: -apotheme - d.rayon }, rayon: d.rayon });
        return { contours, cercles, secteurs, plis };
    }
    // Prisme droit et pyramide : une base, et des faces latérales en bande.
    const n = nbCotes(d.base);
    const bas = sommetsBase(d.base, d.coteBase, d.profondeurBase, 0);
    if (bas === null || !(d.hauteur > 0))
        return null;
    /* La bande latérale se déroule en suivant les VRAIES longueurs des côtés :
       un rectangle a deux côtés différents, et une bande de largeurs égales
       dessinerait le développement d'un autre solide. */
    const longueurs = bas.map((p, i) => {
        const q = bas[(i + 1) % n];
        return Math.hypot(q.x - p.x, q.z - p.z);
    });
    let x = 0;
    for (let i = 0; i < n; i += 1) {
        const l = longueurs[i];
        if (d.forme === 'prisme_droit') {
            contours.push(rect(x, 0, l, d.hauteur));
        }
        else {
            /* Face latérale de pyramide : un triangle isocèle dont la hauteur est
               l'apothème de la face, déduit lui aussi. */
            const apothemeBase = Math.hypot(bas[i].x, bas[i].z) * Math.cos(Math.PI / n);
            const apothemeFace = Math.hypot(d.hauteur, apothemeBase);
            contours.push([
                { x, y: 0 },
                { x: x + l, y: 0 },
                { x: x + l / 2, y: apothemeFace },
            ]);
        }
        if (i > 0)
            plis.push([
                { x, y: 0 },
                { x, y: d.hauteur },
            ]);
        x += l;
    }
    /* Les bases. Un prisme en a DEUX — une sous la bande, une au-dessus ; une
       pyramide n'en a qu'une. L'oubli de la seconde donnait un patron dont
       l'aire était inférieure d'exactement une base à l'aire totale du solide,
       ce que le test d'aire a montré du premier coup. */
    contours.push(bas.map((p) => ({ x: p.x + x / 2, y: p.z - 2 * d.hauteur })));
    if (d.forme === 'prisme_droit') {
        contours.push(bas.map((p) => ({ x: p.x + x / 2, y: p.z + 3 * d.hauteur })));
    }
    return { contours, cercles, secteurs, plis };
}
//# sourceMappingURL=solides.js.map